import React, { FC, useMemo, useState } from "react";
import { Collapse, Tooltip } from "@mui/material";
import { ShoppingCart } from "@mui/icons-material";
import {
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  ReceiptText,
  Send,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { GetRechnungDetailsDto } from "../../../../api-client";
import { formatDateUI } from "../../../../helpers/dateFormat";
import { Button as ShadButton } from "@/components/ui/button";
import { Badge as ShadBadge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PayRechnungDialogShad from "../dialogs/PayRechnungDialogShad";
import SendBillEmailDialog from "../dialogs/SendBillEmailDialog";
import { useDownload } from "../../../../hooks/useDownload";
import DeleteConfirmationDialogShad from "../dialogs/DeleteConfirmationDialogShad";
import { paymentStatus } from "../../../Transactions/Kasse/v2/types/kasse.types";
import { useDeletePatientRechnung } from "../../../../stores/Transactions/transaction.mutations";
import { useUserId } from "../../../../stores/User/User.hooks";

interface RechnungTableProps {
  rechnungen: GetRechnungDetailsDto[];
  patientId: number;
}

const thClass = "h-6 py-0 text-center text-sm font-semibold uppercase tracking-wide text-slate-600";
const subThClass =
  "h-6 py-0 text-center text-[13px] font-semibold uppercase tracking-wide text-slate-500";

/**
 * Folder-style shadcn tab attached to the table top. Active uses the "Rechnung
 * erstellen" maroon gradient (`group` enables state-aware badge colours).
 */
const tabTriggerClass =
  "group -mb-px inline-flex cursor-pointer items-center gap-1.5 rounded-t border border-b-0 " +
  "border-solid border-slate-200 bg-slate-100 px-3.5 py-1.5 text-sm font-semibold text-slate-600 " +
  "shadow-none transition-colors hover:bg-slate-200 data-[state=active]:border-[#8a4150] " +
  "data-[state=active]:bg-gradient-to-b data-[state=active]:from-[#a5515f] " +
  "data-[state=active]:to-[#8a4150] data-[state=active]:text-white data-[state=active]:shadow-none";

/** Count badge inside each tab; turns light on the active (maroon) tab. */
const tabBadgeClass =
  "inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-slate-200 px-1.5 py-0 " +
  "text-xs font-bold text-slate-600 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white";

/**
 * Drop-in replacement for `NewPatientRechnungTable` using the SEPA card design
 * (shadcn Card + table, centered uppercase headers, zebra rows, teal tabs).
 * Functionality, data and props are identical to the original; only the
 * presentation changes. Dialogs and download/delete logic are reused unchanged.
 */
const RechnungTable: FC<RechnungTableProps> = ({ rechnungen, patientId }) => {
  // Get current user ID
  const userId = useUserId();

  // Mutations
  const deleteRechnungMut = useDeletePatientRechnung();

  // Local state
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [selectedRechnung, setSelectedRechnung] = useState<GetRechnungDetailsDto | null>(null);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRechnung, setDeleteRechnung] = useState<GetRechnungDetailsDto | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRechnung, setEmailRechnung] = useState<GetRechnungDetailsDto | null>(null);

  // Hooks
  const { handleDownloadPdf } = useDownload();

  // Derived data: split into active vs deleted
  const activeRechnungen = useMemo(() => rechnungen.filter((r) => !r.deletedAt), [rechnungen]);
  const deletedRechnungen = useMemo(() => rechnungen.filter((r) => !!r.deletedAt), [rechnungen]);

  const isDeletedView = activeTab === 1;
  const displayedRechnungen = isDeletedView ? deletedRechnungen : activeRechnungen;

  // Handlers
  const handleOpenPayDialog = (rechnung: GetRechnungDetailsDto) => {
    setSelectedRechnung(rechnung);
    setPayDialogOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPayDialogOpen(false);
    setSelectedRechnung(null);
  };

  const handleToggleRow = (rechnungNumber: string) => {
    setOpenRows((prev) => ({
      ...prev,
      [rechnungNumber]: !prev[rechnungNumber],
    }));
  };

  const handleOpenDeleteDialog = (rechnung: GetRechnungDetailsDto) => {
    setDeleteRechnung(rechnung);
    setDeleteDialogOpen(true);
  };

  const handleOpenEmailDialog = (rechnung: GetRechnungDetailsDto) => {
    setEmailRechnung(rechnung);
    setEmailDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteRechnung(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteRechnung) return;

    deleteRechnungMut.mutate(
      {
        rechnungId: deleteRechnung.id,
        patientId: patientId,
        rxId: deleteRechnung.rxId,
      },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setDeleteRechnung(null);
        },
      },
    );
  };

  // Total columns: expand + 9 data columns + (Aktionen | Gelöscht am) = 11
  const colSpan = 11;

  // Helper: escalating severity colour per bill reminder level (amber → orange → red)
  const getMahnungColor = (level: number) => {
    switch (level) {
      case 1:
        return "#f59e0b"; // amber
      case 2:
        return "#ea580c"; // orange
      case 3:
        return "#dc2626"; // red
      default:
        return "#dc2626";
    }
  };

  return (
    <>
      <Card className="mt-3 overflow-hidden rounded-lg border border-solid border-black shadow-none">
        <CardHeader className="space-y-2 bg-slate-50/60 py-4">
          <CardTitle className="flex items-center gap-2 text-xl font-semibold text-slate-800">
            <ReceiptText className="h-5 w-5 text-slate-500" />
            Rechnungen
          </CardTitle>
          <p className="my-0 text-left text-sm text-slate-500">
            Erstellte Rechnungen und Zahlungseingänge des Patienten.
          </p>
        </CardHeader>

        <CardContent className="pt-0">
          <Tabs
            value={isDeletedView ? "deleted" : "active"}
            onValueChange={(v) => setActiveTab(v === "deleted" ? 1 : 0)}
          >
            <TabsList className="flex h-auto w-full justify-end gap-1.5 rounded-none bg-transparent p-0 pr-1">
              <TabsTrigger value="active" className={tabTriggerClass}>
                Aktiv
                <span className={tabBadgeClass}>{activeRechnungen.length}</span>
              </TabsTrigger>
              <TabsTrigger value="deleted" className={tabTriggerClass}>
                Gelöscht
                <span className={tabBadgeClass}>{deletedRechnungen.length}</span>
              </TabsTrigger>
            </TabsList>
            <div className="overflow-hidden rounded-b">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <TableHead className="w-8" />
                    <TableHead className={`${thClass} text-left`}>Rechnungnummer</TableHead>
                    <TableHead className={`${thClass} text-left`}>Rezeptnummer</TableHead>
                    <TableHead className={`${thClass} text-left`}>Patient</TableHead>
                    <TableHead className={`${thClass} text-left`}>Motiv</TableHead>
                    <TableHead className={`${thClass} text-left`}>Erstellt am</TableHead>
                    <TableHead className={`${thClass} text-right`}>Gesamtpreis (€)</TableHead>
                    <TableHead className={`${thClass} text-right`}>Bezahlt (€)</TableHead>
                    <TableHead className={`${thClass} text-right`}>Fällig (€)</TableHead>
                    <TableHead className={thClass}>Dokumente</TableHead>
                    <TableHead className={thClass}>
                      {isDeletedView ? "Gelöscht am" : "Aktionen"}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {displayedRechnungen.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={colSpan}
                        className="py-10 text-center text-sm text-slate-400"
                      >
                        {isDeletedView
                          ? "Keine gelöschten Rechnungen vorhanden"
                          : "Keine Rechnungen vorhanden"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedRechnungen.map((item, index) => {
                      const isOpen = openRows[item.rechnungNumber] || false;
                      const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";

                      return (
                        <React.Fragment key={item.rechnungNumber}>
                          {/* Main row with invoice details */}
                          <TableRow
                            className={`text-base ${zebra} hover:bg-slate-100/70 ${
                              isDeletedView ? "line-through decoration-[#999] opacity-60" : ""
                            }`}
                          >
                            {/* Expand/Collapse Icon Cell */}
                            <TableCell className="w-8 py-1 pl-4 pr-0">
                              <button
                                type="button"
                                onClick={() => handleToggleRow(item.rechnungNumber)}
                                aria-label="expand row"
                                className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-slate-400 hover:text-slate-600"
                              >
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="py-1 text-left">
                              <span className="inline-flex items-center justify-start gap-1.5">
                                <span className="font-medium text-slate-800">
                                  {item.rechnungNumber}
                                </span>
                                {item.billReminders && item.billReminders.length > 0 && (
                                  <Tooltip title="Rechnung hat Mahnung(en) - Vorsicht beim Löschen!">
                                    <span className="inline-flex">
                                      <ShadBadge
                                        variant="outline"
                                        className="gap-1 rounded-full border-amber-200 bg-amber-200 px-2 py-0.5 text-sm font-normal text-amber-700"
                                      >
                                        <TriangleAlert className="h-3 w-3" />
                                        Mahnung
                                      </ShadBadge>
                                    </span>
                                  </Tooltip>
                                )}
                              </span>
                            </TableCell>
                            <TableCell className="py-1 text-left text-slate-700">
                              {item.rxNumber ? (
                                item.rxNumber
                              ) : (
                                <ShoppingCart fontSize="small" className="text-slate-400" />
                              )}
                            </TableCell>
                            <TableCell className="py-1 text-left text-slate-700">
                              {`${item.patientLastName}, ${item.patientFirstName}`}
                            </TableCell>
                            <TableCell className="py-1 text-left text-slate-700">
                              {item.text || ""}
                            </TableCell>
                            <TableCell className="whitespace-nowrap py-1 text-left text-slate-600">
                              {formatDateUI(item.createdAt)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap py-1 text-right font-semibold text-slate-800">
                              {Number(item.amount).toFixed(2).replace(".", ",")} €
                            </TableCell>
                            <TableCell className="whitespace-nowrap py-1 text-right text-slate-700">
                              {Number(item.totalPaid).toFixed(2).replace(".", ",")} €
                            </TableCell>
                            <TableCell className="whitespace-nowrap py-1 text-right text-slate-700">
                              {Number(item.due).toFixed(2).replace(".", ",")} €
                            </TableCell>

                            {/* Dokumente column: Rechnung PDF + Mahnung PDFs */}
                            <TableCell className="py-1">
                              <div className="flex items-center justify-center gap-1">
                                {item.docDto?.id && (
                                  <div className="relative inline-flex">
                                    <Tooltip title="Rechnung PDF">
                                      <ShadButton
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6"
                                        onClick={() => handleDownloadPdf(item.docDto!.id)}
                                      >
                                        <FileText />
                                      </ShadButton>
                                    </Tooltip>
                                    {item.emailSentAt && (
                                      <Tooltip
                                        title={`Rechnung per E-Mail gesendet am ${formatDateUI(item.emailSentAt)}`}
                                      >
                                        <span className="absolute -left-0.5 -top-0.5 inline-flex items-center justify-center text-[#2e7d32]">
                                          <CheckCheck size={16} />
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                )}
                                {item.billReminders
                                  ?.slice()
                                  .sort((a, b) => a.level - b.level)
                                  .map((reminder) =>
                                    reminder.document?.id ? (
                                      <span
                                        key={reminder.id}
                                        className="relative inline-flex items-center"
                                      >
                                        <Tooltip
                                          title={`${reminder.level}. Mahnung – ${formatDateUI(reminder.createdAt)}`}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => handleDownloadPdf(reminder.document!.id)}
                                            className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-[5px]"
                                          >
                                            <span
                                              className="flex h-6 w-6 items-center justify-center rounded-md text-[14px] font-bold leading-none text-white"
                                              style={{
                                                backgroundColor: getMahnungColor(reminder.level),
                                              }}
                                            >
                                              {reminder.level}
                                            </span>
                                          </button>
                                        </Tooltip>
                                        {(reminder.emailSentAt || reminder.sentByEmail) && (
                                          <Tooltip
                                            title={
                                              reminder.emailSentAt
                                                ? `Per E-Mail gesendet am ${formatDateUI(reminder.emailSentAt)}`
                                                : "Per E-Mail gesendet"
                                            }
                                          >
                                            <span className="absolute -left-0.5 -top-0.5 inline-flex items-center justify-center text-[#2e7d32]">
                                              <CheckCheck size={15} />
                                            </span>
                                          </Tooltip>
                                        )}
                                      </span>
                                    ) : null,
                                  )}
                              </div>
                            </TableCell>

                            {/* Actions / Deleted-at column */}
                            <TableCell className="py-1">
                              {isDeletedView ? (
                                <p className="my-0 text-center text-sm text-slate-500">
                                  {formatDateUI(item.deletedAt!)}
                                </p>
                              ) : (
                                <div className="flex items-center justify-end gap-1">
                                  {item.paymentStatus === paymentStatus.GKV_COPAYMENT_REQUESTED ? (
                                    <ShadBadge
                                      variant="outline"
                                      className="gap-1 border-emerald-200 bg-emerald-200 px-2.5 py-1 text-sm font-medium text-emerald-800"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                      GKV Zuzahlung angefordert
                                    </ShadBadge>
                                  ) : Number(item.due.toFixed(2)) > 0 ? (
                                    <>
                                      <ShadButton
                                        size="sm"
                                        className="h-7 rounded border-0 bg-gradient-to-b from-[#5aa6c4] to-[#4189a8] px-1.5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(65,137,168,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-200 hover:from-[#4f9cbb] hover:to-[#3a7f9c] hover:shadow-[0_2px_8px_rgba(65,137,168,0.45),inset_0_1px_0_rgba(255,255,255,0.25)] focus-visible:ring-2 focus-visible:ring-[#5aa6c4]/40 focus-visible:ring-offset-1 active:scale-[0.97]"
                                        onClick={() => handleOpenPayDialog(item)}
                                      >
                                        Ausbuchen
                                      </ShadButton>
                                      <Tooltip title="Rechnung löschen">
                                        <ShadButton
                                          size="icon"
                                          className="h-7 w-7 border-0 bg-red-600 text-white shadow-none transition-all duration-200 hover:scale-110 hover:bg-red-700 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 [&_svg]:size-5"
                                          onClick={() => handleOpenDeleteDialog(item)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </ShadButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <ShadBadge
                                      variant="outline"
                                      className="gap-1 border-emerald-200 bg-emerald-200 px-2.5 py-1 text-sm font-medium text-emerald-800"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                      Vollständig bezahlt
                                    </ShadBadge>
                                  )}
                                  <Tooltip
                                    title={
                                      item.patientEmail
                                        ? "Per E-Mail senden"
                                        : "Keine E-Mail-Adresse hinterlegt"
                                    }
                                  >
                                    <span className="inline-flex">
                                      <ShadButton
                                        size="icon"
                                        className="h-7 w-7 border-0 bg-[#223b63] text-white shadow-none transition-all duration-200 hover:scale-110 hover:bg-[#24546b] focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 disabled:bg-slate-300 [&_svg]:size-5"
                                        onClick={() => handleOpenEmailDialog(item)}
                                        disabled={!item.patientEmail}
                                      >
                                        <Send className="h-4 w-4" />
                                      </ShadButton>
                                    </span>
                                  </Tooltip>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Collapsible row for allocations (bank transfers) */}
                          <TableRow className="hover:bg-transparent">
                            <TableCell colSpan={colSpan} className="p-0">
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <div className="m-3">
                                  <p className="my-0 mb-2 text-base font-semibold text-slate-700">
                                    Banküberweisungen
                                  </p>
                                  <div className="overflow-hidden rounded border border-slate-200">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="border-b border-solid border-slate-200 bg-slate-50 hover:bg-slate-50">
                                          <TableHead className={subThClass}>
                                            Transaktionnummer
                                          </TableHead>
                                          <TableHead className={subThClass}>Bezahlt am</TableHead>
                                          <TableHead className={subThClass}>Erstellt von</TableHead>
                                          <TableHead className={`${subThClass} text-right`}>
                                            Bezahlter Betrag (€)
                                          </TableHead>
                                          <TableHead className={subThClass}>Dokument</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {item.allocations && item.allocations.length > 0 ? (
                                          item.allocations.map((alloc) => (
                                            <TableRow
                                              key={alloc.transactionNumber}
                                              className="text-[15px] hover:bg-slate-100/70"
                                            >
                                              <TableCell className="py-1 text-center text-slate-700">
                                                {alloc.transactionNumber}
                                              </TableCell>
                                              <TableCell className="whitespace-nowrap py-1 text-center text-slate-600">
                                                {formatDateUI(alloc.createdAt)}
                                              </TableCell>
                                              <TableCell className="py-1 text-center text-slate-700">
                                                {`${alloc.userLastName}, ${alloc.userFirstName}`}
                                              </TableCell>
                                              <TableCell className="whitespace-nowrap py-1 text-right text-slate-700">
                                                {Number(alloc.paidAmount)
                                                  .toFixed(2)
                                                  .replace(".", ",")}{" "}
                                                €
                                              </TableCell>
                                              <TableCell className="py-1 text-center">
                                                <button
                                                  type="button"
                                                  onClick={() => {}}
                                                  className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#367b9a] hover:text-[#367b9a]"
                                                >
                                                  <FileText size={20} />
                                                </button>
                                              </TableCell>
                                            </TableRow>
                                          ))
                                        ) : (
                                          <TableRow>
                                            <TableCell
                                              colSpan={5}
                                              className="py-4 text-center text-sm text-slate-400"
                                            >
                                              Keine Banküberweisungen vorhanden
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <PayRechnungDialogShad
        open={payDialogOpen}
        onClose={() => setPayDialogOpen(false)}
        rechnung={selectedRechnung}
        accountId={selectedRechnung?.accountId || ""}
        userId={userId || ""}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialogShad
        open={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Rechnung löschen?"
        content="Möchten Sie diese Rechnung wirklich löschen? Der Vorgang kann rückgängig gemacht werden."
      />

      {/* Send Bill Email Dialog */}
      {emailRechnung && (
        <SendBillEmailDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          rechnung={emailRechnung}
        />
      )}
    </>
  );
};

export default RechnungTable;
